import { createContext, useContext, useState } from 'react';
import { Platform } from 'react-native';
import AppConstants from '../AppConstants';
import { useGeo } from './GeoContext';

export const APIContext = createContext();

export const APIProvider = ({ children }) => {
  const SERVER_URL = "https://cms.nowhere-app.com";
  const geo = useGeo();

  const FLAG_STARTED_LOCATION = 1
  const FLAG_FINISHED_LOCATION = 2
  const FLAG_ARRIVED_AT_LOCATION = 3
  const FLAG_SELECTED_LOCATION = 4
  const FLAG_STARTED_PRELUDE = 6
  const FLAG_FINISHED_PRELUDE = 7
  const FLAG_STARTED_OVERTURE = 8
  const FLAG_FINISHED_OVERTURE = 9
  const FLAG_STARTED_PERFORMANCE = 10
  const FLAG_FINISHED_PERFORMANCE = 11
  
  // Loading state managed externally by JourneyContext
  const [isLoading, setIsLoading] = useState(false);
  const [serverData, setServerData] = useState(null);
  const [isGettingNextLocation, setIsGettingNextLocation] = useState(false);

  // HANDLE SERVER DATA
  const fetchData = async (voyagerCode) => {    
    if (voyagerCode && !(isLoading)) {
      setIsLoading(true);
      const dataURL = `${SERVER_URL}/getdata.json?c=${voyagerCode}`;     
      console.log("Fetching data from ", dataURL); 
      
      try {
        const response = await fetch(dataURL);      
        const responseJson = await response.json();
        setServerData(responseJson);
        return responseJson;
      } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getDeviceInfo = () => {
    return `${Platform.OS} ${Platform.Version}`;
  };

  const hasAvailableLocations = async (voyagerCode) => {
    const rawResponse = await fetch(SERVER_URL + '/hasfreelocations.json?c=' + voyagerCode);
    const responseJson = await rawResponse.json();

    if (responseJson.free === "finished") {
      return "finished";
    } else if (responseJson.free === "yes") {
      return "yes"
    } else {
      return "no";
    }
  };

  const signin = async (email, code) => {
    //setIsLoading(true);
    let sentData = {
      e: email,
      key: code
    };
    
    try {
      const response = await fetch(`${SERVER_URL}/login.json`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sentData),
      });
      const responseJson = await response.json();
      console.log("Signin response:", responseJson);
      if (responseJson.success === 'false') {
        throw new Error('Error signing in');
      } else {
        return responseJson.success === 'true'; // returns a boolean if successful
      }
    } catch (error) {
      console.log('Error signing in:', error);
      throw error;
    } finally {
      //setIsLoading(false);
    }
  };

  const getNextLocation = async (voyagerCode, selectedLocation) => {

    if (isGettingNextLocation === false) {
      setIsGettingNextLocation(true);
      
      try {
        let query = `${SERVER_URL}/nextloc?c=${voyagerCode}`;
        if (selectedLocation) {
          query = query + `&l=${selectedLocation.id}`;
        }
        
        const response = await fetch(query);
        const responseJson = await response.json();
        const locid = responseJson.locid;
        const location = getLocationById(locid);
        return location;
      } catch (error) {
        console.error('Error getting next location:', error);
        throw error;
      } finally {        
        setIsGettingNextLocation(false);
      }
    }
  };

  const startedPrelude = async (voyagerCode) => {
    await checkin(voyagerCode, null, null, null, "Started Prelude", FLAG_STARTED_PRELUDE);
  }
  const finishedPrelude = async (voyagerCode) => {
    await checkin(voyagerCode, null, null, null, "Finished Prelude", FLAG_FINISHED_PRELUDE);
  }

  const startedOverture = async (voyagerCode) => {
    await checkin(voyagerCode, null, null, null, "Started Overture", FLAG_STARTED_OVERTURE);
  }
  const finishedOverture = async (voyagerCode) => {
    await checkin(voyagerCode, null, null, null, "Finished Overture", FLAG_FINISHED_OVERTURE);
  }

  const checkin = async (voyagerCode, latitude, longitude, targetLocation, message, flag) => {

    let inRange = false;
    const distanceCutoff = 1500;

    if (!!voyagerCode) {
      let sentData = {
        e: message,
        key: voyagerCode,
        v: AppConstants.version
      };

      if (flag) {
        sentData.f = flag;
      }

      if (targetLocation !== null) {
        sentData.l = targetLocation.id;
      }

      if (latitude !== null && longitude !== null) {
        sentData.lt = latitude;
        sentData.ln = longitude;
      }

      if (targetLocation !== null && latitude !== null && longitude !== null) {
        let distance = geo.distanceToLocation(targetLocation);
        if (distance < distanceCutoff) {
          inRange = true;
        }
      }

      if (inRange === true || targetLocation === null || latitude === null || longitude === null) {
        await fetch(`${SERVER_URL}/checkin`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(sentData),
        });
      }
    }
  }

  // Helper function to get location by ID from server data
  function getLocationById(locationId) {
    let result = null;
    if (serverData?.locations) {
      // sometiemes it's a string and sometimes a number so we just use == here      
      // eslint-disable-next-line eqeqeq
      result = serverData.locations.find(location => location.id == locationId);
    } else {
      console.warn("No server data available to get location by ID");
    }
    return result;
  }

  // Server notification methods (moved from AppStorage)
  const sendArrivalNotification = async (location, voyagerCode) => {
    try {
      const deviceinfo = getDeviceInfo();
      const sentData = {
        e: "Arrived",
        key: voyagerCode,
        f: FLAG_ARRIVED_AT_LOCATION,
        v: AppConstants.VERSION_ID,
        l: location.id,
        d: deviceinfo 
      };

      const response = await fetch(`${SERVER_URL}/checkin`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sentData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      console.error('Error sending arrival notification:', error);
      throw error;
    }
  };

  const sendFeedback = async (contentitem, feedback, voyagerCode) => {
    if (!feedback || feedback.length <= 1) {
      return; // Don't send empty feedback
    }

    try {
      const deviceinfo = getDeviceInfo();
      const sentData = {
        e: "Feedback",
        k: voyagerCode,
        v: AppConstants.VERSION_ID,
        c: contentitem.id,
        t: feedback,  
        d: deviceinfo 
      };

      const response = await fetch(`${SERVER_URL}/feedbacks`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sentData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      console.error('Error sending feedback:', error);
      throw error;
    }
  };

  const sendHelpMessage = async (voyagerId, latitude, longitude, targetLocation, messageText) => {
    if (!messageText || messageText.length <= 1) {
      return; // Don't send empty messages
    }

    try {
      const deviceinfo = getDeviceInfo();
      const sentData = {
        k: voyagerId,
        v: AppConstants.VERSION_ID,
        lt: latitude,
        ln: longitude,
        t: messageText,
        d: deviceinfo 
      };

      const response = await fetch(`${SERVER_URL}/helpmessages`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sentData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response;
    } catch (error) {
      console.error('Error sending help message:', error);
      throw error;
    }
  };


  return (
    <APIContext.Provider value={{
      SERVER_URL,
      signin,
      hasAvailableLocations,
      isLoading,
      setIsLoading,
      serverData,
      setServerData,
      isGettingNextLocation,
      fetchData,
      getNextLocation,
      getLocationById,
      sendArrivalNotification,
      sendFeedback,
      sendHelpMessage,
      checkin,
      startedPrelude,
      finishedPrelude,
      startedOverture,
      finishedOverture
    }}>
      {children}
    </APIContext.Provider>
  );
};

// Custom hook for using API context
export const useAPI = () => {
  const context = useContext(APIContext);
  if (!context) {
    throw new Error('useAPI must be used within an APIProvider');
  }
  return context;
};

export default APIProvider;